import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { formatTimestamp } from "~/utils/helpers";
import { toPx } from "~/utils/components";
import { useIntersectionObserver } from "~/hooks/useIntersectionObserver";
import { useLabels } from "~/hooks/useLabels";
import { useScrollWithShadow } from "~/hooks/useScrollWithShadow";
import { useUserContext } from "~/hooks/useUserContext";

import { SheetModal } from "../sheetModal/SheetModal";
import { TranscriptContent } from "./TranscriptContent";
import { Entity } from "../entity/Entity";
import { Divider } from "../divider/Divider";
import { Menu } from "../menu/Menu";
import { MenuItem } from "../menu/MenuItem";
import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";

import "./Transcript.css";

import type { SyntheticEvent } from "react";
import type { ActionProp } from "../components";
import type { TranscriptData } from "~/utils/transcript";

export interface TranscriptState {
  selectedTimestamps: number[];
  selectedText: string;
}

export interface TranscriptProps {
  actions?: (state: TranscriptState) => ActionProp[];
  currentTimestamp?: number;
  data?: TranscriptData;
  postTitle?: string;
  isDesktopLayout?: boolean;
}

export interface TranscriptRef {
  closeActionsModal: () => void;
}

const DEFAULT_LABELS = {
  "common.cta.cancel": "Cancel",
  "transcript.cta.return": "Return Transcript to Current Play Time",
};

export const Transcript = forwardRef<TranscriptRef, TranscriptProps>(
  function Transcript(
    { actions, currentTimestamp, data, postTitle, isDesktopLayout },
    ref,
  ) {
    const { t } = useLabels({ fallback: DEFAULT_LABELS });

    const containerRef = useRef<HTMLDivElement>(null);

    const {
      preferences: { reducedMotion },
    } = useUserContext();

    const [scrollBehavior, setScrollBeahvior] =
      useState<ScrollIntoViewOptions["behavior"]>("auto");

    const [scrollContainerRef, scrollWithShadow] =
      useScrollWithShadow<HTMLDivElement>();

    const [activeSegmentRef, intersection] =
      useIntersectionObserver<HTMLParagraphElement>({
        root: scrollContainerRef.current,
        // Give some extra space to account for the bar
        rootMargin: "0px 0px -60px 0px",
      });

    const [isInteracting, setIsInteracting] = useState(false);
    const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
    const [isActionsOpen, setIsActionsModalOpen] = useState(false);

    const customProperties = useMemo(() => {
      return {
        // Button should fill the container, minus scrollbars
        "--transcript-resume-button-width": toPx(scrollWithShadow.width),
      };
    }, [scrollWithShadow.width]);

    const preventAutoScroll = useMemo(() => {
      return isInteracting || Boolean(selectedNodeIds.length);
    }, [isInteracting, selectedNodeIds.length]);

    const getNodeTimestamp = useCallback((id: string) => {
      // Parse the node id ({index}:{timestamp}) and extract the timestamp
      return Number(id.replace(/^\d+:/, ""));
    }, []);

    // Locate the node by id and extract the text
    const getNodeText = useCallback(
      (id: string) => {
        return data?.segments
          .flatMap((segment) => segment.entries.flatMap((entry) => entry.nodes))
          .find((node) => node.id === id)?.text;
      },
      [data?.segments],
    );

    const selectedTimestamps = useMemo(() => {
      return selectedNodeIds
        .map(getNodeTimestamp)
        .filter((value, index, array) => array.indexOf(value) === index)
        .sort((a, b) => a - b);
    }, [getNodeTimestamp, selectedNodeIds]);

    const selectedText = useMemo(() => {
      return selectedNodeIds
        .sort((a, b) => getNodeTimestamp(a) - getNodeTimestamp(b))
        .map(getNodeText)
        .join(" ");
    }, [getNodeText, getNodeTimestamp, selectedNodeIds]);

    const handleCloseActionsModal = useCallback(() => {
      setSelectedNodeIds([]);
      setIsActionsModalOpen(false);
    }, []);

    const resumeButton = useMemo(() => {
      if (!isInteracting || !intersection || !intersection.rootBounds) {
        return null;
      }

      const icon =
        intersection.rootBounds.bottom > intersection.boundingClientRect.top
          ? "arrow-up"
          : "arrow-down";

      function handleClick() {
        setIsInteracting(false);
      }

      return (
        <button className="Transcript__resume-button" onClick={handleClick}>
          {<Icon icon={icon} size="sm" />}
          {t("transcript.cta.return")}
        </button>
      );
    }, [isInteracting, intersection, t]);

    const content = useMemo(() => {
      function handleScroll(event: SyntheticEvent) {
        setIsInteracting(true);
        scrollWithShadow.onScroll(event);
      }

      function handleSelect(id: string) {
        setSelectedNodeIds((nodes) => {
          const isSelected = selectedNodeIds.includes(id);
          return isSelected ? nodes.filter((n) => n !== id) : nodes.concat(id);
        });

        setIsActionsModalOpen(true);
      }

      if (!isDesktopLayout) {
        return (
          <div onTouchMove={handleScroll} onWheel={handleScroll}>
            <TranscriptContent
              activeSegmentRef={activeSegmentRef}
              currentTimestamp={currentTimestamp}
              data={data}
              preventAutoScroll={preventAutoScroll}
              selectedNodeIds={selectedNodeIds}
              scrollBehavior={scrollBehavior}
              onSelect={handleSelect}
            />
          </div>
        );
      }

      return (
        <>
          <div
            ref={scrollContainerRef}
            className="Transcript__scroll-outer"
            onTouchMove={handleScroll}
            onWheel={handleScroll}
            // because this is scrollable, keyboard users need a way to tab to it
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          >
            <div className="Transcript__scroll-inner">
              <TranscriptContent
                activeSegmentRef={activeSegmentRef}
                currentTimestamp={currentTimestamp}
                data={data}
                preventAutoScroll={preventAutoScroll}
                selectedNodeIds={selectedNodeIds}
                scrollBehavior={scrollBehavior}
                onSelect={handleSelect}
              />
            </div>
          </div>
          <div
            className="Transcript__scroll-shadows"
            style={{ boxShadow: scrollWithShadow.boxShadow }}
          />
        </>
      );
    }, [
      activeSegmentRef,
      currentTimestamp,
      data,
      preventAutoScroll,
      scrollBehavior,
      scrollContainerRef,
      scrollWithShadow,
      selectedNodeIds,
      isDesktopLayout,
    ]);

    const actionsModal = useMemo(() => {
      const title = selectedTimestamps.map(formatTimestamp).join(", ");

      const state: TranscriptState = {
        selectedTimestamps,
        selectedText,
      };

      return (
        <SheetModal
          disableBackdrop
          isOpen={isActionsOpen}
          root={isDesktopLayout ? containerRef.current : undefined}
          onRequestClose={handleCloseActionsModal}
        >
          <SheetModal.Body>
            <Entity title={title} postTitle={postTitle} />

            <Divider />

            <Menu>
              {actions?.(state).map(({ icon, iconVariant, text, onClick }) => (
                <MenuItem
                  key={text}
                  icon={icon}
                  iconVariant={iconVariant}
                  onClick={onClick}
                >
                  {text}
                </MenuItem>
              ))}
            </Menu>

            <Button
              appearance="secondary"
              fill
              style={{ marginBlockStart: "var(--size-2)" }}
              onClick={handleCloseActionsModal}
            >
              {t("common.cta.cancel")}
            </Button>
          </SheetModal.Body>
        </SheetModal>
      );
    }, [
      actions,
      isDesktopLayout,
      handleCloseActionsModal,
      isActionsOpen,
      postTitle,
      selectedText,
      selectedTimestamps,
      t,
    ]);

    useImperativeHandle(ref, () => ({
      closeActionsModal: handleCloseActionsModal,
    }));

    useEffect(() => {
      // Switch to smooth scrolling once rendered
      if (!reducedMotion) {
        setScrollBeahvior("smooth");
      }
    }, [reducedMotion]);

    return (
      <div className="Transcript" style={customProperties} ref={containerRef}>
        {content}
        {resumeButton}
        {actionsModal}
      </div>
    );
  },
);
